import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash, Copy, ArrowsClockwise, Play, Warning } from "@phosphor-icons/react";
import { fmtDate, labelize } from "@/lib/constants";

const DEFAULT_JSON = `{
  "lead": {
    "consumer": {
      "firstName": "Jane",
      "lastName": "Doe",
      "phone": "415-555-0100",
      "email": "jane@example.com"
    },
    "location": {"state": "CA", "zip": "94103"},
    "sub_source": "google-cpc",
    "accident": {"date": "2026-01-15"}
  }
}`;

const DEFAULT_XML = `<lead>
  <first_name>Jane</first_name>
  <last_name>Doe</last_name>
  <phone>415-555-0100</phone>
  <email>jane@example.com</email>
  <state>CA</state>
  <zip>94103</zip>
</lead>`;

export default function FormIntegrations() {
  const [items, setItems] = useState([]);
  const [fields, setFields] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    const [i, f, af, cp] = await Promise.all([
      api.get("/integrations"),
      api.get("/integrations/_meta/fields"),
      api.get("/organizations?type=affiliate"),
      api.get("/campaigns"),
    ]);
    setItems(i.data.integrations);
    setFields(f.data.fields);
    setAffiliates(af.data.organizations);
    setCampaigns(cp.data.campaigns);
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-mono">Integrations</div>
          <h1 className="font-chivo font-black text-4xl md:text-5xl uppercase tracking-tighter leading-none">Form integrations</h1>
          <div className="mt-1 text-sm text-zinc-600">JSON + XML mapping adapters for third-party form vendors.</div>
        </div>
        <button onClick={() => setShowNew((v) => !v)} data-testid="new-integration-btn"
          className="bg-black text-white border border-black px-3 py-2 text-xs uppercase tracking-widest hover:translate-x-[2px] hover:translate-y-[2px] hover:brut-shadow-sm transition-transform duration-100">
          {showNew ? "Close wizard" : "New integration"}
        </button>
      </div>

      {showNew && (
        <IntegrationWizard fields={fields} affiliates={affiliates} campaigns={campaigns}
          onCreated={() => { setShowNew(false); load(); }} />
      )}

      <div className="brut-card mt-4 overflow-x-auto">
        <table className="w-full text-sm font-mono min-w-[900px]">
          <thead>
            <tr className="border-b border-black text-[10px] uppercase tracking-widest">
              <th className="py-2 px-4 text-left">Name</th>
              <th className="py-2 px-4 text-left">Endpoint slug</th>
              <th className="py-2 px-4 text-left">Format</th>
              <th className="py-2 px-4 text-left">Affiliate</th>
              <th className="py-2 px-4 text-left">Campaign</th>
              <th className="py-2 px-4 text-left">Mapped fields</th>
              <th className="py-2 px-4 text-left">Active</th>
              <th className="py-2 px-4 text-left">Created</th>
              <th className="py-2 px-4 text-left"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id} className="border-b border-zinc-200" data-testid={`integration-row-${i.id}`}>
                <td className="py-2 px-4 font-semibold">{i.name}</td>
                <td className="py-2 px-4">
                  <button
                    onClick={() => {
                      const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/integration/${i.endpoint_slug}`;
                      navigator.clipboard.writeText(url);
                      toast.success("URL copied");
                    }}
                    className="text-xs hover:underline">
                    /{i.endpoint_slug} <Copy size={10} className="inline" />
                  </button>
                </td>
                <td className="py-2 px-4">{i.format}</td>
                <td className="py-2 px-4">{affiliates.find((a) => a.id === i.affiliate_id)?.name || "—"}</td>
                <td className="py-2 px-4">{campaigns.find((c) => c.id === i.campaign_id)?.name || "—"}</td>
                <td className="py-2 px-4">{Object.keys(i.field_mapping || {}).length}</td>
                <td className="py-2 px-4">{i.is_active ? "yes" : "no"}</td>
                <td className="py-2 px-4 text-[11px]">{fmtDate(i.created_at)}</td>
                <td className="py-2 px-4">
                  <button data-testid={`integration-delete-${i.id}`} onClick={async () => {
                    if (!confirm("Delete integration?")) return;
                    await api.delete(`/integrations/${i.id}`);
                    toast.success("Deleted"); load();
                  }}
                  className="border border-red-600 text-red-700 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-red-600 hover:text-white">
                    <Trash size={10} className="inline" /> Delete
                  </button>
                </td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={9} className="py-6 text-center label-mono">No integrations yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function IntegrationWizard({ fields, affiliates, campaigns, onCreated }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "", format: "json", affiliate_id: "", campaign_id: "",
    sample_payload: DEFAULT_JSON, is_active: true, static_values: {},
  });
  const [mapping, setMapping] = useState({});
  const [detectedKeys, setDetectedKeys] = useState([]);
  const [testResult, setTestResult] = useState(null);
  const [creating, setCreating] = useState(false);

  // detect keys from sample
  useEffect(() => {
    if (form.format === "json") {
      try {
        const obj = JSON.parse(form.sample_payload || "{}");
        setDetectedKeys(flatten(obj));
      } catch { setDetectedKeys([]); }
    } else {
      try {
        const doc = new DOMParser().parseFromString(form.sample_payload || "", "text/xml");
        if (doc.querySelector("parsererror")) { setDetectedKeys([]); return; }
        setDetectedKeys(flattenXml(doc.documentElement, ""));
      } catch { setDetectedKeys([]); }
    }
  }, [form.sample_payload, form.format]);

  const applySuggestion = () => {
    // Simple fuzzy auto-suggest: match on lowercased last path segment
    const next = { ...mapping };
    detectedKeys.forEach((k) => {
      const seg = k.split(".").pop().toLowerCase().replace(/[^a-z0-9]/g, "_");
      const candidates = {
        first_name: ["first_name", "firstname", "first"],
        last_name: ["last_name", "lastname", "last", "surname"],
        phone: ["phone", "phone_number", "mobile", "cell"],
        email: ["email", "email_address"],
        state: ["state", "st"],
        zip: ["zip", "zipcode", "postal_code", "postcode"],
        city: ["city"],
        address: ["address", "street"],
        county: ["county"],
        accident_date: ["accident_date", "date_of_accident", "incident_date"],
        external_lead_id: ["lead_id", "external_id", "id"],
        trustedform_url: ["trustedform_url", "trustedform", "tf_cert"],
        sub_1: ["sub_1", "sub1", "sub_source", "source"],
        sub_2: ["sub_2", "sub2"], sub_3: ["sub_3", "sub3"],
        sub_4: ["sub_4", "sub4"], sub_5: ["sub_5", "sub5"],
      };
      for (const [canon, needles] of Object.entries(candidates)) {
        if (needles.includes(seg)) { next[k] = canon; break; }
      }
    });
    setMapping(next);
    toast.success("Auto-mapping applied");
  };

  const testMapping = async () => {
    try {
      const sample = form.format === "json" ? JSON.parse(form.sample_payload) : form.sample_payload;
      // Post to a virtual test endpoint via create+delete would be heavy; use a fake integration by posting the wizard state through a create then a test call.
      // Instead we compute preview client-side using the same _apply_mapping logic
      const source = form.format === "json" ? JSON.parse(form.sample_payload) : parseXml(form.sample_payload);
      const mapped = {};
      Object.entries(mapping).forEach(([src, tgt]) => {
        if (!fields.includes(tgt)) return;
        const val = digPath(source, src);
        if (val != null && val !== "") mapped[tgt] = val;
      });
      setTestResult({ mapped });
    } catch (e) { toast.error(String(e)); }
  };

  const submit = async () => {
    if (!form.name) return toast.error("Name required");
    if (Object.keys(mapping).length === 0) return toast.error("Map at least one field");
    setCreating(true);
    try {
      const payload = {
        name: form.name, format: form.format,
        affiliate_id: form.affiliate_id || null,
        campaign_id: form.campaign_id || null,
        field_mapping: mapping,
        static_values: form.static_values,
        sample_payload: form.sample_payload,
        is_active: form.is_active,
      };
      await api.post("/integrations", payload);
      toast.success("Integration created");
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setCreating(false); }
  };

  return (
    <div className="brut-card p-5 mb-4" data-testid="integration-wizard">
      <div className="flex items-center gap-2 mb-4">
        {[1, 2, 3].map((n) => (
          <div key={n} className={`px-3 py-1 border border-black text-xs uppercase tracking-widest ${step === n ? "bg-black text-white" : ""}`}>Step {n}</div>
        ))}
      </div>

      {step === 1 && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label-mono">Name*</label>
              <input data-testid="wiz-name" required className="w-full border border-black px-3 py-2 mt-1"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Legal Match XML feed" />
            </div>
            <div>
              <label className="label-mono">Format</label>
              <select data-testid="wiz-format" className="w-full border border-black px-2 py-1.5 mt-1"
                value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value, sample_payload: e.target.value === "json" ? DEFAULT_JSON : DEFAULT_XML })}>
                <option value="json">JSON</option>
                <option value="xml">XML</option>
              </select>
            </div>
            <div>
              <label className="label-mono">Affiliate</label>
              <select data-testid="wiz-affiliate" className="w-full border border-black px-2 py-1.5 mt-1"
                value={form.affiliate_id} onChange={(e) => setForm({ ...form, affiliate_id: e.target.value })}>
                <option value="">— none —</option>
                {affiliates.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label-mono">Campaign</label>
              <select data-testid="wiz-campaign" className="w-full border border-black px-2 py-1.5 mt-1"
                value={form.campaign_id} onChange={(e) => setForm({ ...form, campaign_id: e.target.value })}>
                <option value="">— none —</option>
                {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="label-mono">Sample payload (auto-detects keys)</label>
            <textarea data-testid="wiz-sample" rows={10}
              className="w-full border border-black px-3 py-2 mt-1 font-mono text-[11px]"
              value={form.sample_payload} onChange={(e) => setForm({ ...form, sample_payload: e.target.value })} />
            <div className="text-[11px] text-zinc-500 mt-1">Detected keys: {detectedKeys.length}</div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setStep(2)} data-testid="wiz-next-1"
              className="bg-black text-white border border-black px-3 py-2 text-xs uppercase tracking-widest">Next → Mapping</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm">Map each source path to a canonical field. Unmapped keys are ignored.</div>
            <button onClick={applySuggestion} data-testid="wiz-autosuggest"
              className="border border-black px-3 py-1.5 text-xs uppercase tracking-widest hover:bg-black hover:text-white">
              <ArrowsClockwise size={12} className="inline mr-1" /> Auto-suggest
            </button>
          </div>
          <div className="border border-black overflow-hidden">
            <div className="grid grid-cols-2 border-b border-black bg-zinc-100 text-[10px] uppercase tracking-widest">
              <div className="px-3 py-2">Source path</div>
              <div className="px-3 py-2 border-l border-black">Canonical field</div>
            </div>
            <div className="max-h-[400px] overflow-y-auto">
              {detectedKeys.map((k) => (
                <div key={k} className="grid grid-cols-2 border-b border-zinc-200" data-testid={`wiz-row-${k}`}>
                  <div className="px-3 py-2 font-mono text-xs">{k}</div>
                  <div className="border-l border-zinc-200">
                    <select data-testid={`wiz-map-${k}`}
                      className="w-full px-2 py-2 text-xs font-mono border-0"
                      value={mapping[k] || ""}
                      onChange={(e) => setMapping({ ...mapping, [k]: e.target.value })}>
                      <option value="">— skip —</option>
                      {fields.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                </div>
              ))}
              {!detectedKeys.length && <div className="p-6 text-center label-mono">No keys detected — go back and paste a sample</div>}
            </div>
          </div>
          <div className="flex justify-between">
            <button onClick={() => setStep(1)} className="border border-black px-3 py-2 text-xs uppercase tracking-widest">← Back</button>
            <button onClick={() => setStep(3)} data-testid="wiz-next-2"
              className="bg-black text-white border border-black px-3 py-2 text-xs uppercase tracking-widest">Next → Test</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <button onClick={testMapping} data-testid="wiz-test"
            className="border border-black px-3 py-2 text-xs uppercase tracking-widest hover:bg-black hover:text-white">
            <Play size={12} className="inline mr-1" /> Test mapping
          </button>
          {testResult && (
            <div className="border border-black p-3">
              <div className="label-mono mb-2">Result</div>
              <pre className="font-mono text-xs whitespace-pre-wrap">{JSON.stringify(testResult.mapped, null, 2)}</pre>
              {(!testResult.mapped.first_name || !testResult.mapped.last_name) && (
                <div className="mt-2 border border-red-600 bg-red-50 p-2 text-xs text-red-700 flex items-center gap-2">
                  <Warning size={14} /> Required first_name/last_name did not resolve — fix your mapping.
                </div>
              )}
            </div>
          )}
          <div className="flex justify-between">
            <button onClick={() => setStep(2)} className="border border-black px-3 py-2 text-xs uppercase tracking-widest">← Back</button>
            <button onClick={submit} disabled={creating} data-testid="wiz-submit"
              className="bg-black text-white border border-black px-4 py-2 uppercase text-xs tracking-widest font-bold hover:translate-x-[2px] hover:translate-y-[2px] hover:brut-shadow-sm transition-transform duration-100 disabled:opacity-60">
              {creating ? "Creating…" : "Create integration"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// helpers
function flatten(obj, prefix = "") {
  const out = [];
  if (obj == null) return out;
  if (typeof obj !== "object" || Array.isArray(obj)) { out.push(prefix); return out; }
  for (const k of Object.keys(obj)) {
    const path = prefix ? `${prefix}.${k}` : k;
    const v = obj[k];
    if (v && typeof v === "object" && !Array.isArray(v)) out.push(...flatten(v, path));
    else out.push(path);
  }
  return out;
}

function flattenXml(elem, prefix) {
  const out = [];
  if (!elem) return out;
  const children = Array.from(elem.children);
  for (const c of children) {
    const path = prefix ? `${prefix}.${c.tagName}` : c.tagName;
    if (c.children.length > 0) out.push(...flattenXml(c, path));
    else out.push(path);
  }
  return out;
}

function parseXml(str) {
  const doc = new DOMParser().parseFromString(str, "text/xml");
  const toObj = (n) => {
    if (!n.children.length) return (n.textContent || "").trim();
    const o = {};
    Array.from(n.children).forEach((c) => { o[c.tagName] = toObj(c); });
    return o;
  };
  return toObj(doc.documentElement);
}

function digPath(obj, path) {
  return path.split(".").reduce((a, k) => (a == null ? a : a[k]), obj);
}
